import { SubmitEvent, useCallback, useEffect, useState } from "react"
import { Text, Button, Input } from "../../../components"
import { Modal } from "../../get-qr-admin"
import { CrmClientRef, CrmRequest, PAGE_SIZE, copyToClipboard, ensureOk, formatDateTime, getErrorMessage, usePagedList } from "../api"
import { ConfirmButton, ListStatus, Pager } from "../components"

type CodeFilter = 'all' | 'free' | 'busy'

const FILTERS: {key: CodeFilter; label: string; query: string}[] = [
    {key: 'all', label: 'Все', query: ''},
    {key: 'free', label: 'Свободные', query: 'onlyFree=true'},
    {key: 'busy', label: 'Занятые', query: 'onlyBusy=true'},
]

// 'all' — все карты, 'none' — созданные поштучно, без папки, число — id папки
type FolderKey = 'all' | 'none' | number

const BATCH_MAX = 1000

const getCardUrl = (code: string) => `${window.location.origin}/cards/${encodeURIComponent(code)}`

interface CrmCode {
    id: number;
    code: string;
    store_id: number | null;
    batch_id: number | null;
    store_title: string | null;
    client: CrmClientRef | null;
}

interface CodeBatch {
    id: number;
    name: string;
    created_at: string;
    total: number;
    busy: number;
}

interface BatchStats {
    total: number;
    busy: number;
}

const getFolderQuery = (folder: FolderKey) => {
    if (folder === 'all') return ''
    if (folder === 'none') return 'noBatch=true'
    return `batch_id=${folder}`
}

export const CodesTab = ({request}: {request: CrmRequest}) => {
    const [filter, setFilter] = useState<CodeFilter>('all')
    const [folder, setFolder] = useState<FolderKey>('all')
    const query = [FILTERS.find((f) => f.key === filter)?.query, getFolderQuery(folder)].filter(Boolean).join('&')
    const {items, loading, error, offset, setOffset, hasNext, reload} = usePagedList<CrmCode>(request, `/crm/get-codes${query ? `?${query}` : ''}`, 'codes')
    const [batches, setBatches] = useState<CodeBatch[]>([])
    const [noBatch, setNoBatch] = useState<BatchStats>({total: 0, busy: 0})
    const [busy, setBusy] = useState(false)
    const [actionError, setActionError] = useState<string | null>(null)
    const [copiedId, setCopiedId] = useState<number | null>(null)
    const [downloadingId, setDownloadingId] = useState<number | null>(null)
    const [movingCode, setMovingCode] = useState<CrmCode | null>(null)
    const [creatingBatch, setCreatingBatch] = useState<{name: string; count: string} | null>(null)
    const [batchError, setBatchError] = useState<string | null>(null)

    const loadBatches = useCallback(async () => {
        try {
            const res = await request('/crm/get-code-batches')
            if (!res) return
            await ensureOk(res, 'Не удалось загрузить папки')
            const data = await res.json()
            setBatches(data.batches ?? [])
            setNoBatch(data.no_batch ?? {total: 0, busy: 0})
        } catch (err) {
            setActionError(getErrorMessage(err, 'Не удалось загрузить папки'))
        }
    }, [request])

    useEffect(() => {
        loadBatches()
    }, [loadBatches])

    const reloadAll = () => Promise.all([reload(), loadBatches()])

    const copyCardUrl = async (code: CrmCode) => {
        setActionError(null)
        try {
            await copyToClipboard(getCardUrl(code.code))
            setCopiedId(code.id)
            setTimeout(() => setCopiedId((id) => (id === code.id ? null : id)), 1500)
        } catch {
            setActionError('Не удалось скопировать ссылку')
        }
    }

    // PDF отдаётся только с токеном, поэтому качаем через fetch и сохраняем через blob-ссылку
    const downloadQrPdf = async (code: CrmCode) => {
        setDownloadingId(code.id)
        setActionError(null)
        try {
            const res = await request(`/crm/get-card-image?code_id=${code.id}`)
            if (!res) return
            await ensureOk(res, 'Не удалось скачать PDF')
            const url = URL.createObjectURL(await res.blob())
            const link = document.createElement('a')
            link.href = url
            link.download = `qr-${code.code}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        } catch (err) {
            setActionError(getErrorMessage(err, 'Не удалось скачать PDF'))
        } finally {
            setDownloadingId(null)
        }
    }

    const createCode = async () => {
        setBusy(true)
        setActionError(null)
        try {
            // Если открыта папка — карта создаётся сразу в ней
            const res = await request(`/crm/create-code${typeof folder === 'number' ? `?batch_id=${folder}` : ''}`, {method: 'POST'})
            if (!res) return
            await ensureOk(res, 'Не удалось создать карту')
            await reloadAll()
        } catch (err) {
            setActionError(getErrorMessage(err, 'Не удалось создать карту'))
        } finally {
            setBusy(false)
        }
    }

    const createBatch = async (e: SubmitEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!creatingBatch) return
        const name = creatingBatch.name.trim()
        const count = Number(creatingBatch.count)
        if (!name) {
            setBatchError('Укажите название папки')
            return
        }
        if (!Number.isInteger(count) || count < 1 || count > BATCH_MAX) {
            setBatchError(`Количество — от 1 до ${BATCH_MAX}`)
            return
        }
        setBusy(true)
        setBatchError(null)
        try {
            const res = await request(`/crm/create-code-batch?name=${encodeURIComponent(name)}&count=${count}`, {method: 'POST'})
            if (!res) return
            await ensureOk(res, 'Не удалось создать карты')
            const data = await res.json()
            setCreatingBatch(null)
            await loadBatches()
            selectFolder(data.batch.id)
        } catch (err) {
            setBatchError(getErrorMessage(err, 'Не удалось создать карты'))
        } finally {
            setBusy(false)
        }
    }

    const deleteBatch = async (batchId: number) => {
        setBusy(true)
        setActionError(null)
        try {
            const res = await request(`/crm/delete-code-batch?batch_id=${batchId}`, {method: 'DELETE'})
            if (!res) return
            await ensureOk(res, 'Не удалось удалить папку')
            await loadBatches()
            selectFolder('all')
        } catch (err) {
            setActionError(getErrorMessage(err, 'Не удалось удалить папку'))
        } finally {
            setBusy(false)
        }
    }

    const moveCode = async (code: CrmCode, batchId: number | null) => {
        setBusy(true)
        setActionError(null)
        try {
            const res = await request(`/crm/move-code?code_id=${code.id}${batchId !== null ? `&batch_id=${batchId}` : ''}`, {method: 'POST'})
            if (!res) return
            await ensureOk(res, 'Не удалось перенести карту')
            setMovingCode(null)
            // Карта могла уйти из текущей папки — если страница опустела, шагаем назад
            if (folder !== 'all' && items.length === 1 && offset > 0) setOffset(offset - PAGE_SIZE)
            await reloadAll()
        } catch (err) {
            setActionError(getErrorMessage(err, 'Не удалось перенести карту'))
            setMovingCode(null)
        } finally {
            setBusy(false)
        }
    }

    const deleteCode = async (codeId: number) => {
        setBusy(true)
        setActionError(null)
        try {
            const res = await request(`/crm/delete-code?code_id=${codeId}`, {method: 'DELETE'})
            if (!res) return
            await ensureOk(res, 'Не удалось удалить карту')
            loadBatches()
            if (items.length === 1 && offset > 0) setOffset(offset - PAGE_SIZE)
            else await reload()
        } catch (err) {
            setActionError(getErrorMessage(err, 'Не удалось удалить карту'))
        } finally {
            setBusy(false)
        }
    }

    const selectFilter = (key: CodeFilter) => {
        setFilter(key)
        setOffset(0)
    }

    const selectFolder = (key: FolderKey) => {
        setFolder(key)
        setOffset(0)
    }

    const selectedBatch = typeof folder === 'number' ? batches.find((b) => b.id === folder) : undefined
    const allStats = batches.reduce((acc, b) => ({total: acc.total + b.total, busy: acc.busy + b.busy}), noBatch)

    const folderChip = (key: FolderKey, label: string, stats: BatchStats, hint?: string) => (
        <button
            key={key}
            type="button"
            className={`crm-folder${folder === key ? ' crm-folder-active' : ''}`}
            onClick={() => selectFolder(key)}
        >
            <Text size="s" color={folder === key ? 'white' : 'lightGray'}>{label}</Text>
            <span className="admin-link-metric">{`${stats.busy}/${stats.total} занято${hint ? ` · ${hint}` : ''}`}</span>
        </button>
    )

    return (
        <div className="crm-list">
            <div className="crm-add-actions">
                <button type="button" className="link-row admin-add-row" onClick={() => {setCreatingBatch({name: '', count: '100'}); setBatchError(null)}} disabled={busy}>
                    <span className="link-icon admin-add-icon">+</span>
                    <Text size="m" color="lightGray">Создать пачку карт</Text>
                </button>
                <button type="button" className="link-row admin-add-row" onClick={createCode} disabled={busy}>
                    <span className="link-icon admin-add-icon">+</span>
                    <Text size="m" color="lightGray">{busy ? 'Подождите…' : selectedBatch ? `Одна карта в «${selectedBatch.name}»` : 'Одна карта'}</Text>
                </button>
            </div>

            {actionError && <Text size="xs" color="accent">{actionError}</Text>}

            <div className="crm-folders">
                {folderChip('all', 'Все карты', allStats)}
                {batches.map((b) => folderChip(b.id, b.name, b, formatDateTime(b.created_at)))}
                {noBatch.total > 0 && folderChip('none', 'Без папки', noBatch)}
            </div>

            {selectedBatch && (
                <div className="crm-folder-header">
                    <div className="admin-link-info">
                        <Text size="m" color="white">{selectedBatch.name}</Text>
                        <span className="admin-link-metric">{`Создана ${formatDateTime(selectedBatch.created_at)} · ${selectedBatch.total} карт, занято ${selectedBatch.busy}`}</span>
                    </div>
                    {selectedBatch.busy === 0 && (
                        <ConfirmButton label="Удалить папку" disabled={busy} onConfirm={() => deleteBatch(selectedBatch.id)} />
                    )}
                </div>
            )}

            <div className="admin-login-tabs">
                {FILTERS.map(({key, label}) => (
                    <div key={key} className={`admin-login-tab${filter === key ? ' admin-login-tab-active' : ''}`}>
                        <Button variant="ghost" fullWidth textSize="s" textColor={filter === key ? 'white' : 'lightGray'} onClick={() => selectFilter(key)}>{label}</Button>
                    </div>
                ))}
            </div>

            <ListStatus loading={loading} error={error} empty={items.length === 0} emptyText="Карт нет" />

            {!loading && items.map((code) => (
                <div className="link-row admin-link-row" key={code.id}>
                    <div className="admin-link-info">
                        <Text size="m" color="white">{code.code}</Text>
                        <div className="crm-code-meta">
                            <span className="admin-link-metric">{`#${code.id}`}</span>
                            {code.store_id ? (
                                <a
                                    className="crm-code-status crm-code-status-busy"
                                    href={`/${code.store_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {`Занята · ${code.store_title || `страница #${code.store_id}`}`}
                                </a>
                            ) : (
                                <span className="crm-code-status crm-code-status-free">Свободна</span>
                            )}
                            {code.client && (
                                <span className="admin-link-metric">
                                    {`Владелец: ${code.client.login}${code.client.mail ? ` (${code.client.mail})` : ''} · #${code.client.id}`}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="crm-row-actions">
                        <button type="button" className="admin-edit-btn" title={getCardUrl(code.code)} onClick={() => copyCardUrl(code)}>
                            <Text size="xs" color={copiedId === code.id ? 'accent' : 'lightGray'}>{copiedId === code.id ? 'Скопировано' : 'Скопировать'}</Text>
                        </button>
                        <button type="button" className="admin-edit-btn" disabled={downloadingId === code.id} onClick={() => downloadQrPdf(code)}>
                            <Text size="xs" color="lightGray">{downloadingId === code.id ? 'Загрузка…' : 'Скачать PDF'}</Text>
                        </button>
                        <button type="button" className="admin-edit-btn" disabled={busy} onClick={() => setMovingCode(code)}>
                            <Text size="xs" color="lightGray">В папку</Text>
                        </button>
                        <ConfirmButton label="Удалить" disabled={busy} onConfirm={() => deleteCode(code.id)} />
                    </div>
                </div>
            ))}

            <Pager offset={offset} hasNext={hasNext} loading={loading} onChange={setOffset} />

            {movingCode && (
                <Modal title={`Карта #${movingCode.id} в папку`} onClose={() => setMovingCode(null)}>
                    <div className="admin-profile-edit">
                        <div className="crm-move-list">
                            {[...batches.map((b) => ({id: b.id as number | null, name: b.name, hint: `${b.total} карт · ${formatDateTime(b.created_at)}`})), {id: null, name: 'Без папки', hint: 'убрать из папки'}].map((target) => {
                                const current = movingCode.batch_id === target.id
                                return (
                                    <button
                                        key={target.id ?? 'none'}
                                        type="button"
                                        className={`crm-folder crm-move-target${current ? ' crm-folder-active' : ''}`}
                                        disabled={busy || current}
                                        onClick={() => moveCode(movingCode, target.id)}
                                    >
                                        <Text size="s" color={current ? 'white' : 'lightGray'}>{target.name}</Text>
                                        <span className="admin-link-metric">{current ? 'текущая папка' : target.hint}</span>
                                    </button>
                                )
                            })}
                        </div>
                        {batches.length === 0 && <Text size="xs" color="lightGray">Папок пока нет — создайте пачку карт, и она появится здесь</Text>}
                    </div>
                </Modal>
            )}

            {creatingBatch && (
                <Modal title="Новая пачка карт" onClose={() => setCreatingBatch(null)}>
                    <form className="admin-profile-edit" onSubmit={createBatch}>
                        <Input
                            label="Название папки"
                            autoComplete="off"
                            value={creatingBatch.name}
                            onChange={(e) => setCreatingBatch({...creatingBatch, name: e.target.value})}
                        />
                        <Input
                            label={`Количество карт (до ${BATCH_MAX})`}
                            type="number"
                            inputMode="numeric"
                            min={1}
                            max={BATCH_MAX}
                            value={creatingBatch.count}
                            onChange={(e) => setCreatingBatch({...creatingBatch, count: e.target.value})}
                        />
                        <Text size="xs" color="lightGray">Дата создания подставится автоматически</Text>

                        {batchError && <Text size="xs" color="accent">{batchError}</Text>}

                        <div className="admin-edit-actions">
                            <Button type="button" variant="ghost" textSize="s" textColor="lightGray" onClick={() => setCreatingBatch(null)} disabled={busy}>Отмена</Button>
                            <Button type="submit" variant="solid" textSize="s" disabled={busy}>
                                {busy ? 'Создание…' : 'Создать'}
                            </Button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    )
}
